import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Lightbulb, Plus, ThumbsUp, ThumbsDown, Loader2, RefreshCw, Search, Filter,
  User, Calendar, ArrowRight, CheckCircle2, XCircle, Clock, MessageSquare, Rocket,
  Edit2, Eye, DollarSign, Target, Trash2, Send
} from 'lucide-react';
import api from '../api';

const statusConfig = {
  submitted: { label: 'Submitted', color: 'bg-blue-100 text-blue-700', icon: Clock },
  under_review: { label: 'Under Review', color: 'bg-amber-100 text-amber-700', icon: MessageSquare },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700', icon: XCircle },
  converted: { label: 'Converted', color: 'bg-purple-100 text-purple-700', icon: Rocket }
};

const priorityConfig = {
  low: { label: 'Low', color: 'bg-slate-100 text-slate-700' },
  medium: { label: 'Medium', color: 'bg-blue-100 text-blue-700' },
  high: { label: 'High', color: 'bg-orange-100 text-orange-700' },
  urgent: { label: 'Urgent', color: 'bg-red-100 text-red-700' }
};

const CHANNELS = [
  { value: 'email', label: 'Email' },
  { value: 'social_media', label: 'Social Media' },
  { value: 'paid_ads', label: 'Paid Ads' },
  { value: 'content', label: 'Content Marketing' },
  { value: 'events', label: 'Events' },
  { value: 'seo', label: 'SEO' },
  { value: 'pr', label: 'PR' },
  { value: 'partnerships', label: 'Partnerships' }
];

const campaignTypes = [
  { value: 'promotional', label: 'Promotional' },
  { value: 'awareness', label: 'Brand Awareness' },
  { value: 'engagement', label: 'Engagement' },
  { value: 'retention', label: 'Retention' },
  { value: 'product_launch', label: 'Product Launch' },
  { value: 'event', label: 'Event' },
  { value: 'seasonal', label: 'Seasonal' }
];

export const MarketingIdeationPage = () => {
  const { user, hasRole } = useAuth();
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('all');
  
  // Create idea dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedIdea, setSelectedIdea] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [allowAnonymous, setAllowAnonymous] = useState(false);
  const [productLines, setProductLines] = useState([]);
  const [noteInput, setNoteInput] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  
  const [newIdea, setNewIdea] = useState({
    title: '',
    description: '',
    is_anonymous: false,
    target_audience: '',
    suggested_channels: [],
    estimated_impact: '',
    suggested_timeline: '',
    tags: [],
    product_lines: [],
    // Campaign details
    campaign_type: '',
    estimated_budget: '',
    expected_roi: '',
    start_date: '',
    end_date: ''
  });
  
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    fetchIdeas();
    fetchSettings();
    fetchProductLines();
  }, [statusFilter]);

  const fetchIdeas = async () => {
    try {
      setRefreshing(true);
      const params = new URLSearchParams();
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      const res = await api.get(`/marketing/ideation?${params.toString()}`);
      setIdeas(res.data || []);
    } catch (error) {
      console.error('Failed to fetch ideas:', error);
      toast.error('Failed to load ideas');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await api.get('/settings/crm');
      setAllowAnonymous(res.data?.allow_anonymous_ideas || false);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const fetchProductLines = async () => {
    try {
      const res = await api.get('/product-lines');
      setProductLines(res.data || []);
    } catch (error) {
      console.error('Failed to fetch product lines:', error);
    }
  };

  const handleSubmitIdea = async () => {
    if (!newIdea.title.trim() || !newIdea.description.trim()) {
      toast.error('Title and description are required');
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        ...newIdea,
        estimated_budget: newIdea.estimated_budget ? parseFloat(newIdea.estimated_budget) : null,
        expected_roi: newIdea.expected_roi ? parseFloat(newIdea.expected_roi) : null
      };
      await api.post('/marketing/ideation', payload);
      toast.success('Idea submitted successfully!');
      setShowCreateDialog(false);
      resetIdeaForm();
      fetchIdeas();
    } catch (error) {
      console.error('Failed to submit idea:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit idea');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditIdea = (idea) => {
    setSelectedIdea(idea);
    setNewIdea({
      title: idea.title || '',
      description: idea.description || '',
      is_anonymous: idea.is_anonymous || false,
      target_audience: idea.target_audience || '',
      suggested_channels: idea.suggested_channels || [],
      estimated_impact: idea.estimated_impact || '',
      suggested_timeline: idea.suggested_timeline || '',
      tags: idea.tags || [],
      product_lines: idea.product_lines || [],
      campaign_type: idea.campaign_type || '',
      estimated_budget: idea.estimated_budget || '',
      expected_roi: idea.expected_roi || '',
      start_date: idea.start_date || '',
      end_date: idea.end_date || ''
    });
    setShowEditDialog(true);
  };

  const handleUpdateIdea = async () => {
    if (!selectedIdea) return;
    
    try {
      setSubmitting(true);
      const payload = {
        ...newIdea,
        estimated_budget: newIdea.estimated_budget ? parseFloat(newIdea.estimated_budget) : null,
        expected_roi: newIdea.expected_roi ? parseFloat(newIdea.expected_roi) : null
      };
      await api.put(`/marketing/ideation/${selectedIdea.id}`, payload);
      toast.success('Idea updated!');
      setShowEditDialog(false);
      setSelectedIdea(null);
      resetIdeaForm();
      fetchIdeas();
    } catch (error) {
      toast.error('Failed to update idea');
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewIdea = (idea) => {
    setSelectedIdea(idea);
    setShowDetailDialog(true);
  };

  const handleAddNote = async () => {
    if (!selectedIdea || !noteInput.trim()) return;
    
    try {
      setAddingNote(true);
      await api.post(`/marketing/ideation/${selectedIdea.id}/notes?content=${encodeURIComponent(noteInput)}`);
      toast.success('Note added');
      setNoteInput('');
      // Refresh idea to get updated notes
      const res = await api.get(`/marketing/ideation/${selectedIdea.id}`);
      setSelectedIdea(res.data);
      fetchIdeas();
    } catch (error) {
      toast.error('Failed to add note');
    } finally {
      setAddingNote(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!selectedIdea) return;
    
    try {
      await api.delete(`/marketing/ideation/${selectedIdea.id}/notes/${noteId}`);
      toast.success('Note deleted');
      // Refresh idea
      const res = await api.get(`/marketing/ideation/${selectedIdea.id}`);
      setSelectedIdea(res.data);
      fetchIdeas();
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  const resetIdeaForm = () => {
    setNewIdea({
      title: '',
      description: '',
      is_anonymous: false,
      target_audience: '',
      suggested_channels: [],
      estimated_impact: '',
      suggested_timeline: '',
      tags: [],
      product_lines: [],
      campaign_type: '',
      estimated_budget: '',
      expected_roi: '',
      start_date: '',
      end_date: ''
    });
  };

  const handleVote = async (ideaId, voteType) => {
    try {
      const res = await api.post(`/marketing/ideation/${ideaId}/vote?vote_type=${voteType}`);
      // Update local state
      setIdeas(ideas.map(idea => 
        idea.id === ideaId 
          ? { ...idea, upvotes: Array(res.data.upvotes).fill(''), downvotes: Array(res.data.downvotes).fill('') }
          : idea
      ));
    } catch (error) {
      console.error('Failed to vote:', error);
      toast.error('Failed to register vote');
    }
  };

  const handleUpdateStatus = async (ideaId, newStatus, reviewNotes = '') => {
    try {
      const res = await api.put(`/marketing/ideation/${ideaId}`, { status: newStatus, review_notes: reviewNotes });
      
      // Show special message when submitting for review (creates draft campaign)
      if (newStatus === 'under_review' && res.data?.new_campaign_id) {
        toast.success('Idea submitted for review! Draft campaign created in Marketing Campaigns.');
      } else {
        toast.success(`Idea ${newStatus}`);
      }
      fetchIdeas();
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update idea');
    }
  };

  const handleConvertToCampaign = async (ideaId) => {
    try {
      const res = await api.post(`/marketing/ideation/${ideaId}/convert`);
      toast.success('Idea converted to campaign!');
      fetchIdeas();
    } catch (error) {
      console.error('Failed to convert idea:', error);
      toast.error(error.response?.data?.detail || 'Failed to convert idea');
    }
  };

  const addTag = () => {
    if (tagInput.trim() && !newIdea.tags.includes(tagInput.trim())) {
      setNewIdea(prev => ({ ...prev, tags: [...prev.tags, tagInput.trim()] }));
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setNewIdea(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));
  };

  const toggleChannel = (channel) => {
    setNewIdea(prev => ({
      ...prev,
      suggested_channels: prev.suggested_channels.includes(channel)
        ? prev.suggested_channels.filter(c => c !== channel)
        : [...prev.suggested_channels, channel]
    }));
  };

  const filteredIdeas = ideas.filter(idea => {
    const matchesSearch = 
      idea.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      idea.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (activeTab === 'my') {
      return matchesSearch && idea.submitted_by === user?.id;
    }
    return matchesSearch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="marketing-ideation-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Lightbulb className="h-6 w-6 text-amber-500" />
            Marketing Ideation Board
          </h1>
          <p className="text-muted-foreground">
            Submit and vote on marketing campaign ideas
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchIdeas} disabled={refreshing}>
            {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
          <Button onClick={() => setShowCreateDialog(true)} data-testid="submit-idea-btn">
            <Plus className="h-4 w-4 mr-2" />
            Submit Idea
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search ideas..."
            className="pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="submitted">Submitted</SelectItem>
            <SelectItem value="under_review">Under Review</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="converted">Converted</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">All Ideas ({ideas.length})</TabsTrigger>
          <TabsTrigger value="my">My Ideas ({ideas.filter(i => i.submitted_by === user?.id).length})</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="mt-4">
          <IdeasGrid 
            ideas={filteredIdeas}
            onVote={handleVote}
            onUpdateStatus={handleUpdateStatus}
            onConvert={handleConvertToCampaign}
            onEdit={handleEditIdea}
            onView={handleViewIdea}
            canManage={hasRole(['admin', 'marketing_manager'])}
            currentUserId={user?.id}
          />
        </TabsContent>

        <TabsContent value="my" className="mt-4">
          <IdeasGrid 
            ideas={filteredIdeas}
            onVote={handleVote}
            onUpdateStatus={handleUpdateStatus}
            onConvert={handleConvertToCampaign}
            onEdit={handleEditIdea}
            onView={handleViewIdea}
            canManage={hasRole(['admin', 'marketing_manager'])}
            currentUserId={user?.id}
          />
        </TabsContent>
      </Tabs>

      {/* Create Idea Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lightbulb className="h-5 w-5 text-amber-500" />
              Submit a Marketing Idea
            </DialogTitle>
            <DialogDescription>
              Share your marketing campaign idea with the team
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Idea Title *</Label>
              <Input
                placeholder="Enter a catchy title for your idea..."
                value={newIdea.title}
                onChange={(e) => setNewIdea(prev => ({ ...prev, title: e.target.value }))}
                data-testid="idea-title-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Description *</Label>
              <Textarea
                placeholder="Describe your idea in detail. What problem does it solve? What's the expected outcome?"
                value={newIdea.description}
                onChange={(e) => setNewIdea(prev => ({ ...prev, description: e.target.value }))}
                rows={4}
              />
            </div>
            
            {/* Campaign Details Section */}
            <div className="p-4 bg-slate-50 rounded-lg space-y-4">
              <h4 className="font-medium flex items-center gap-2">
                <Target className="h-4 w-4" />
                Campaign Details (Optional)
              </h4>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Campaign Type</Label>
                  <Select
                    value={newIdea.campaign_type || ''}
                    onValueChange={(v) => setNewIdea(prev => ({ ...prev, campaign_type: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {campaignTypes.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Estimated Budget ($)</Label>
                  <Input
                    type="number"
                    value={newIdea.estimated_budget}
                    onChange={(e) => setNewIdea(prev => ({ ...prev, estimated_budget: e.target.value }))}
                    placeholder="10000"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={newIdea.start_date}
                    onChange={(e) => setNewIdea(prev => ({ ...prev, start_date: e.target.value }))}
                  />
                </div>

                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    value={newIdea.end_date}
                    onChange={(e) => setNewIdea(prev => ({ ...prev, end_date: e.target.value }))}
                  />
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Target Audience</Label>
                <Input
                  placeholder="Who is this for?"
                  value={newIdea.target_audience}
                  onChange={(e) => setNewIdea(prev => ({ ...prev, target_audience: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Suggested Timeline</Label>
                <Input
                  placeholder="e.g., Q1 2026, 2 weeks"
                  value={newIdea.suggested_timeline}
                  onChange={(e) => setNewIdea(prev => ({ ...prev, suggested_timeline: e.target.value }))}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Estimated Impact</Label>
              <Select
                value={newIdea.estimated_impact}
                onValueChange={(v) => setNewIdea(prev => ({ ...prev, estimated_impact: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select impact level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low - Minor improvement</SelectItem>
                  <SelectItem value="medium">Medium - Noticeable results</SelectItem>
                  <SelectItem value="high">High - Significant impact</SelectItem>
                  <SelectItem value="transformative">Transformative - Game changer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Suggested Channels</Label>
              <div className="flex flex-wrap gap-2 p-3 border rounded-lg">
                {CHANNELS.map((channel) => (
                  <Badge
                    key={channel.value}
                    variant={newIdea.suggested_channels.includes(channel.value) ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => toggleChannel(channel.value)}
                  >
                    {channel.label}
                  </Badge>
                ))}
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Tags</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Add a tag..."
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                />
                <Button variant="outline" onClick={addTag}>Add</Button>
              </div>
              {newIdea.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {newIdea.tags.map((tag) => (
                    <Badge key={tag} variant="secondary" className="cursor-pointer" onClick={() => removeTag(tag)}>
                      {tag} ×
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            
            {allowAnonymous && (
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <Label>Submit Anonymously</Label>
                  <p className="text-xs text-muted-foreground">Your name will not be shown</p>
                </div>
                <Switch
                  checked={newIdea.is_anonymous}
                  onCheckedChange={(v) => setNewIdea(prev => ({ ...prev, is_anonymous: v }))}
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmitIdea} disabled={submitting} data-testid="submit-idea-form-btn">
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Lightbulb className="h-4 w-4 mr-2" />}
              Submit Idea
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Idea Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="h-5 w-5" />
              Edit Idea
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                value={newIdea.title}
                onChange={(e) => setNewIdea(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Your idea title"
              />
            </div>

            <div className="space-y-2">
              <Label>Description *</Label>
              <Textarea
                value={newIdea.description}
                onChange={(e) => setNewIdea(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe your idea in detail"
                rows={4}
              />
            </div>

            {/* Campaign Details Section */}
            <div className="p-4 bg-slate-50 rounded-lg space-y-4">
              <h4 className="font-medium flex items-center gap-2">
                <Target className="h-4 w-4" />
                Campaign Details
              </h4>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Campaign Type</Label>
                  <Select
                    value={newIdea.campaign_type || ''}
                    onValueChange={(v) => setNewIdea(prev => ({ ...prev, campaign_type: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {campaignTypes.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Estimated Budget ($)</Label>
                  <Input
                    type="number"
                    value={newIdea.estimated_budget}
                    onChange={(e) => setNewIdea(prev => ({ ...prev, estimated_budget: e.target.value }))}
                    placeholder="10000"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Expected ROI (%)</Label>
                  <Input
                    type="number"
                    value={newIdea.expected_roi}
                    onChange={(e) => setNewIdea(prev => ({ ...prev, expected_roi: e.target.value }))}
                    placeholder="150"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Target Audience</Label>
                  <Input
                    value={newIdea.target_audience}
                    onChange={(e) => setNewIdea(prev => ({ ...prev, target_audience: e.target.value }))}
                    placeholder="e.g., Small business owners"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={newIdea.start_date}
                    onChange={(e) => setNewIdea(prev => ({ ...prev, start_date: e.target.value }))}
                  />
                </div>

                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    value={newIdea.end_date}
                    onChange={(e) => setNewIdea(prev => ({ ...prev, end_date: e.target.value }))}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Estimated Impact</Label>
              <Input
                value={newIdea.estimated_impact}
                onChange={(e) => setNewIdea(prev => ({ ...prev, estimated_impact: e.target.value }))}
                placeholder="Expected outcome or impact"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateIdea} disabled={submitting}>
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail/View Dialog with Notes */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lightbulb className="h-5 w-5" />
              {selectedIdea?.title}
            </DialogTitle>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <User className="h-4 w-4" />
              {selectedIdea?.is_anonymous ? 'Anonymous' : selectedIdea?.submitted_by_name}
              <span>•</span>
              <Calendar className="h-4 w-4" />
              {selectedIdea?.created_at && format(new Date(selectedIdea.created_at), 'MMM d, yyyy')}
            </div>
            {/* Show link to campaign if exists */}
            {selectedIdea?.converted_to_campaign_id && (
              <div className="mt-2">
                <Link 
                  to={`/marketing/campaigns`}
                  className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                >
                  <Rocket className="h-4 w-4" />
                  View in Campaigns
                </Link>
              </div>
            )}
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Description */}
            <div>
              <h4 className="font-medium mb-2">Description</h4>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">{selectedIdea?.description}</p>
            </div>

            {/* Campaign Details */}
            {(selectedIdea?.campaign_type || selectedIdea?.estimated_budget) && (
              <div className="p-4 bg-slate-50 rounded-lg">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Target className="h-4 w-4" />
                  Campaign Details
                </h4>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  {selectedIdea?.campaign_type && (
                    <div>
                      <p className="text-muted-foreground">Type</p>
                      <p className="font-medium capitalize">{selectedIdea.campaign_type.replace('_', ' ')}</p>
                    </div>
                  )}
                  {selectedIdea?.estimated_budget && (
                    <div>
                      <p className="text-muted-foreground">Budget</p>
                      <p className="font-medium">${parseFloat(selectedIdea.estimated_budget).toLocaleString()}</p>
                    </div>
                  )}
                  {selectedIdea?.expected_roi && (
                    <div>
                      <p className="text-muted-foreground">Expected ROI</p>
                      <p className="font-medium">{selectedIdea.expected_roi}%</p>
                    </div>
                  )}
                  {selectedIdea?.target_audience && (
                    <div>
                      <p className="text-muted-foreground">Target Audience</p>
                      <p className="font-medium">{selectedIdea.target_audience}</p>
                    </div>
                  )}
                  {selectedIdea?.start_date && (
                    <div>
                      <p className="text-muted-foreground">Timeline</p>
                      <p className="font-medium">
                        {selectedIdea.start_date} {selectedIdea.end_date && `- ${selectedIdea.end_date}`}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Channels & Tags */}
            <div className="flex flex-wrap gap-2">
              {selectedIdea?.suggested_channels?.map(ch => (
                <Badge key={ch} variant="outline">{ch}</Badge>
              ))}
              {selectedIdea?.tags?.map(tag => (
                <Badge key={tag} variant="secondary">{tag}</Badge>
              ))}
            </div>

            {/* Notes Section */}
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                Notes & Updates ({selectedIdea?.notes?.length || 0})
              </h4>

              {/* Add Note */}
              <div className="flex gap-2 mb-4">
                <Input
                  value={noteInput}
                  onChange={(e) => setNoteInput(e.target.value)}
                  placeholder="Add a note or update..."
                  onKeyPress={(e) => e.key === 'Enter' && handleAddNote()}
                />
                <Button onClick={handleAddNote} disabled={addingNote || !noteInput.trim()}>
                  {addingNote ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>

              {/* Notes List */}
              <ScrollArea className="h-[200px]">
                {selectedIdea?.notes?.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">No notes yet</p>
                ) : (
                  <div className="space-y-3">
                    {selectedIdea?.notes?.slice().reverse().map((note) => (
                      <div key={note.id} className="p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2 text-xs">
                            <User className="h-3 w-3" />
                            <span className="font-medium">{note.author_name}</span>
                            <span className="text-muted-foreground">
                              {format(new Date(note.created_at), 'MMM d, yyyy h:mm a')}
                            </span>
                          </div>
                          {(note.author_id === user?.id || hasRole(['admin'])) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0"
                              onClick={() => handleDeleteNote(note.id)}
                            >
                              <Trash2 className="h-3 w-3 text-red-500" />
                            </Button>
                          )}
                        </div>
                        <p className="text-sm">{note.content}</p>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
              Close
            </Button>
            {(hasRole(['admin', 'marketing_manager']) || selectedIdea?.submitted_by === user?.id) && 
             selectedIdea?.status !== 'converted' && (
              <Button onClick={() => {
                setShowDetailDialog(false);
                handleEditIdea(selectedIdea);
              }}>
                <Edit2 className="h-4 w-4 mr-2" />
                Edit Idea
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Ideas Grid Component
const IdeasGrid = ({ ideas, onVote, onUpdateStatus, onConvert, onEdit, onView, canManage, currentUserId }) => {
  if (ideas.length === 0) {
    return (
      <Card>
        <CardContent className="py-16 text-center">
          <Lightbulb className="h-16 w-16 mx-auto text-muted-foreground mb-4 opacity-50" />
          <h3 className="text-lg font-medium mb-2">No Ideas Yet</h3>
          <p className="text-muted-foreground">Be the first to submit a marketing idea!</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {ideas.map((idea) => {
        const status = statusConfig[idea.status] || statusConfig.submitted;
        const StatusIcon = status.icon;
        const upvotes = idea.upvotes?.length || 0;
        const downvotes = idea.downvotes?.length || 0;
        const score = upvotes - downvotes;
        const hasNotes = idea.notes?.length > 0;
        const hasCampaignDetails = idea.campaign_type || idea.estimated_budget || idea.start_date;
        
        return (
          <Card key={idea.id} className="flex flex-col hover:shadow-md transition-shadow">
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between gap-2">
                <CardTitle className="text-base line-clamp-2">{idea.title}</CardTitle>
                <Badge className={status.color}>
                  <StatusIcon className="h-3 w-3 mr-1" />
                  {status.label}
                </Badge>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <User className="h-3 w-3" />
                {idea.is_anonymous ? 'Anonymous' : idea.submitted_by_name}
                <span>•</span>
                <Calendar className="h-3 w-3" />
                {format(new Date(idea.created_at), 'MMM d, yyyy')}
                {hasNotes && (
                  <>
                    <span>•</span>
                    <MessageSquare className="h-3 w-3" />
                    {idea.notes.length}
                  </>
                )}
              </div>
            </CardHeader>
            
            <CardContent className="flex-1">
              <p className="text-sm text-muted-foreground line-clamp-3 mb-3">
                {idea.description}
              </p>
              
              {/* Campaign Details Preview */}
              {hasCampaignDetails && (
                <div className="grid grid-cols-2 gap-2 mb-3 p-2 bg-slate-50 rounded text-xs">
                  {idea.campaign_type && (
                    <div className="flex items-center gap-1">
                      <Target className="h-3 w-3 text-muted-foreground" />
                      <span className="capitalize">{idea.campaign_type.replace('_', ' ')}</span>
                    </div>
                  )}
                  {idea.estimated_budget && (
                    <div className="flex items-center gap-1">
                      <DollarSign className="h-3 w-3 text-muted-foreground" />
                      ${parseFloat(idea.estimated_budget).toLocaleString()}
                    </div>
                  )}
                  {idea.start_date && (
                    <div className="flex items-center gap-1 col-span-2">
                      <Calendar className="h-3 w-3 text-muted-foreground" />
                      {idea.start_date} {idea.end_date && `- ${idea.end_date}`}
                    </div>
                  )}
                </div>
              )}
              
              {idea.suggested_channels?.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-3">
                  {idea.suggested_channels.slice(0, 3).map((channel) => (
                    <Badge key={channel} variant="outline" className="text-xs">
                      {channel}
                    </Badge>
                  ))}
                  {idea.suggested_channels.length > 3 && (
                    <Badge variant="outline" className="text-xs">
                      +{idea.suggested_channels.length - 3}
                    </Badge>
                  )}
                </div>
              )}
              
              {idea.priority && (
                <Badge className={priorityConfig[idea.priority]?.color || ''}>
                  {priorityConfig[idea.priority]?.label || idea.priority}
                </Badge>
              )}
            </CardContent>
            
            <div className="p-4 pt-0 mt-auto">
              {/* View/Edit buttons */}
              <div className="flex items-center gap-2 mb-3">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => onView(idea)}
                >
                  <Eye className="h-4 w-4 mr-1" />
                  View
                </Button>
                {(canManage || idea.submitted_by === currentUserId) && idea.status !== 'converted' && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => onEdit(idea)}
                  >
                    <Edit2 className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                )}
              </div>

              {/* Voting */}
              <div className="flex items-center justify-between border-t pt-3">
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onVote(idea.id, 'up')}
                    className="h-8 px-2"
                  >
                    <ThumbsUp className="h-4 w-4 mr-1" />
                    {upvotes}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onVote(idea.id, 'down')}
                    className="h-8 px-2"
                  >
                    <ThumbsDown className="h-4 w-4 mr-1" />
                    {downvotes}
                  </Button>
                  <span className={`text-sm font-medium ${score > 0 ? 'text-green-600' : score < 0 ? 'text-red-600' : ''}`}>
                    {score > 0 ? '+' : ''}{score}
                  </span>
                </div>
                
                {/* Manager Actions */}
                {canManage && idea.status !== 'converted' && (
                  <div className="flex items-center gap-1">
                    {idea.status === 'submitted' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onUpdateStatus(idea.id, 'under_review')}
                      >
                        Review
                      </Button>
                    )}
                    {idea.status === 'under_review' && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onUpdateStatus(idea.id, 'approved')}
                          className="text-green-600"
                        >
                          <CheckCircle2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onUpdateStatus(idea.id, 'rejected')}
                          className="text-red-600"
                        >
                          <XCircle className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                    {idea.status === 'approved' && (
                      <Button
                        size="sm"
                        onClick={() => onConvert(idea.id)}
                      >
                        <ArrowRight className="h-4 w-4 mr-1" />
                        Convert
                      </Button>
                    )}
                  </div>
                )}
                
                {idea.converted_to_campaign_id && (
                  <Link to={`/marketing/campaigns/${idea.converted_to_campaign_id}`}>
                    <Button variant="link" size="sm">
                      View Campaign
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
};

export default MarketingIdeationPage;
